import {
  Color,
  DoubleSide,
  NormalBlending,
  ShaderMaterial,
  UniformsLib,
  UniformsUtils,
  Vector2,
  type Texture,
} from 'three';
import { Palette } from '../Palette.js';

/**
 * The look of a single shard: video, glass and edge emission in one pass.
 *
 * The brief asks for those three to be *composed* separately but explicitly warns
 * against spending draw calls on it. They are therefore separate **terms in one
 * shader** rather than separate meshes — a stacked-geometry approach would triple
 * the shard count for an effect that is three additions in a fragment.
 *
 * Blending is `NormalBlending`, not additive like the rest of the world. That is
 * the load-bearing decision for "the background must stay black": an additive
 * shard adds its whole colour to empty space, whereas an alpha-blended one over
 * black resolves to `colour x alpha` and can never exceed what it is actually
 * showing. Mirror Drift covers a large area, so additive would have re-created
 * exactly the white-out the previous phase removed.
 */

export interface MirrorMaterialOptions {
  /** Multiplier on the sampled video. Governs how much of it reaches bloom. */
  readonly videoGain?: number;
  /** Base glass opacity before fresnel and edge contributions. */
  readonly baseAlpha?: number;
  readonly edgeGain?: number;
  /** Width of the rim glow, in edge-distance units (0 = rim, 1 = centre). */
  readonly edgeWidth?: number;
  /** 0 picks the cyan edge, 1 the violet one. */
  readonly edgeMix?: number;
  readonly sheenGain?: number;
  readonly opacity?: number;
  readonly brightness?: number;
}

const VERTEX = /* glsl */ `
attribute float aEdge;

uniform vec2 uUvScale;
uniform vec2 uUvOffset;

varying vec2  vUv;
varying float vEdge;
varying vec3  vNormalView;
varying vec3  vViewDir;

#include <fog_pars_vertex>

void main() {
  vEdge = aEdge;

  // Cover-fit the video into the shard's bounding box, computed on the CPU once
  // the intrinsic video size is known.
  vUv = (uv - 0.5) * uUvScale + 0.5 + uUvOffset;

  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  vNormalView = normalMatrix * normal;
  vViewDir = -mvPosition.xyz;

  gl_Position = projectionMatrix * mvPosition;

  #include <fog_vertex>
}
`;

const FRAGMENT = /* glsl */ `
uniform sampler2D uVideo;
uniform float uHasVideo;
uniform float uVideoGain;
uniform float uBaseAlpha;
uniform float uEdgeGain;
uniform float uEdgeWidth;
uniform float uEdgeMix;
uniform float uSheenGain;
uniform float uOpacity;
uniform float uBrightness;
uniform vec3  uEdgeColorA;
uniform vec3  uEdgeColorB;
uniform vec3  uGlassColor;

varying vec2  vUv;
varying float vEdge;
varying vec3  vNormalView;
varying vec3  vViewDir;

#include <fog_pars_fragment>

void main() {
  // abs() rather than a front/back branch: the shard is double sided and must not
  // go black when it tumbles past edge-on.
  vec3 normal = normalize(vNormalView);
  vec3 view = normalize(vViewDir);
  float facing = abs(dot(normal, view));

  vec3 video = vec3(0.0);
  if (uHasVideo > 0.5) {
    video = texture2D(uVideo, clamp(vUv, 0.0, 1.0)).rgb;
  }

  // Oblique shards dim toward their edge-on pose, which is what sells them as
  // flat panes catching light rather than as billboards.
  float oblique = mix(0.10, 1.0, pow(facing, 0.75));
  vec3 videoTerm = video * uVideoGain * oblique;

  // Grazing angles pick up a cool sheen: the cue that reads as a reflective
  // surface rather than a screen.
  float fresnel = pow(1.0 - facing, 3.2);
  vec3 sheen = uGlassColor * fresnel * uSheenGain;

  // aEdge is 0 on the rim and 1 at the centroid, so this is a rim band.
  float edgeMask = smoothstep(uEdgeWidth, 0.0, vEdge);
  vec3 edge = mix(uEdgeColorA, uEdgeColorB, uEdgeMix) * edgeMask * uEdgeGain;

  vec3 color = (videoTerm + sheen + edge) * uBrightness;

  // Alpha rises at the rim and at grazing angles so the shard reads as a solid
  // piece of glass with a defined boundary, not as a fading decal.
  float alpha = uOpacity * clamp(
    uBaseAlpha + fresnel * 0.30 + edgeMask * 0.55,
    0.0, 1.0);

  gl_FragColor = vec4(color, alpha);

  // Distant shards dissolve into the fog rather than hanging in the void.
  #include <fog_fragment>
}
`;

export function createMirrorMaterial(options: MirrorMaterialOptions = {}): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: UniformsUtils.merge([
      UniformsLib['fog'] ?? {},
      {
        uVideo: { value: null },
        uHasVideo: { value: 0 },
        uUvScale: { value: new Vector2(1, 1) },
        uUvOffset: { value: new Vector2(0, 0) },
        uVideoGain: { value: options.videoGain ?? 0.5 },
        uBaseAlpha: { value: options.baseAlpha ?? 0.5 },
        uEdgeGain: { value: options.edgeGain ?? 0.5 },
        uEdgeWidth: { value: options.edgeWidth ?? 0.16 },
        uEdgeMix: { value: options.edgeMix ?? 0 },
        uSheenGain: { value: options.sheenGain ?? 0.18 },
        uOpacity: { value: options.opacity ?? 1 },
        uBrightness: { value: options.brightness ?? 1 },
        uEdgeColorA: { value: new Color().copy(Palette.rippleSafe) },
        uEdgeColorB: { value: new Color().copy(Palette.mandalaEdge) },
        uGlassColor: { value: new Color().copy(Palette.ringCool) },
      },
    ]),
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    transparent: true,
    blending: NormalBlending,
    // Alpha-blended glass must not write depth, or shards drawn early would clip
    // the ones behind them.
    depthWrite: false,
    depthTest: true,
    side: DoubleSide,
    fog: true,
  });
}

/**
 * Sets the UV transform that fits the video into the shard without distortion.
 *
 * "Cover" rather than "contain": a letterboxed shard would show the video's black
 * bars, and black bars inside a light-emitting shard look like a rendering fault.
 */
export function applyCoverFit(
  material: ShaderMaterial,
  videoAspect: number,
  shardAspect: number,
): void {
  const scale = material.uniforms['uUvScale']?.value as Vector2 | undefined;
  if (scale === undefined) return;
  if (videoAspect > shardAspect) {
    scale.set(shardAspect / videoAspect, 1);
  } else {
    scale.set(1, videoAspect / shardAspect);
  }
}

export function setMirrorTexture(material: ShaderMaterial, texture: Texture | null): void {
  const slot = material.uniforms['uVideo'];
  const flag = material.uniforms['uHasVideo'];
  if (slot !== undefined) slot.value = texture;
  if (flag !== undefined) flag.value = texture === null ? 0 : 1;
}
